/**
 * Item model for the canvas right-click menus (Figma "Canvas UI 2.0", node
 * 566:1022). Pure: the workspace page supplies what is currently possible and
 * gets back the sections to render, in Figma order, with dividers between
 * sections.
 *
 *   Canvas: Upload · Asset Manager · Add Nodes | Undo · Redo | Paste
 *           | Project Settings · Send Feedback
 *   Node:   Add to Chat · Save to Library · Download | Copy · Paste · Duplicate
 *           | Project Settings · Delete · Send Feedback
 *
 * The canvas menu's last section mirrors the node menu's (same relative order,
 * minus Delete, which needs a node).
 *
 * Items a node type cannot do at all (Download on Script Writer, Storyboard,
 * Video Editor; Save to Library on Video Editor) are hidden.
 * Items that exist but cannot run right now (Undo with empty history, Paste
 * with an empty clipboard, Download before the node has an output) are shown
 * disabled so the menu keeps its shape.
 */

import type { ContextMenuAction, ContextMenuKind } from '../../../analytics/generated/analyticsEvents'

export type ContextMenuItemId = ContextMenuAction

export interface ContextMenuEntry {
  id: ContextMenuItemId
  disabled: boolean
}

export type ContextMenuSection = ContextMenuEntry[]

/** hidden: no target node has the capability; disabled: some do, none can act now. */
export type CapabilityState = 'hidden' | 'disabled' | 'enabled'

export interface CanvasMenuState {
  canUndo: boolean
  canRedo: boolean
  canPaste: boolean
}

export interface NodeMenuState {
  canPaste: boolean
  saveToLibrary: CapabilityState
  download: CapabilityState
}

const entry = (id: ContextMenuItemId, enabled = true): ContextMenuEntry => ({ id, disabled: !enabled })

function capabilityEntry(id: ContextMenuItemId, state: CapabilityState): ContextMenuEntry[] {
  return state === 'hidden' ? [] : [entry(id, state === 'enabled')]
}

export function buildCanvasMenu(state: CanvasMenuState): ContextMenuSection[] {
  return [
    [entry('upload'), entry('asset_manager'), entry('add_nodes')],
    [entry('undo', state.canUndo), entry('redo', state.canRedo)],
    [entry('paste', state.canPaste)],
    [entry('project_settings'), entry('send_feedback')],
  ]
}

export function buildNodeMenu(state: NodeMenuState): ContextMenuSection[] {
  return [
    [
      entry('add_to_chat'),
      ...capabilityEntry('save_to_library', state.saveToLibrary),
      ...capabilityEntry('download', state.download),
    ],
    [entry('copy'), entry('paste', state.canPaste), entry('duplicate')],
    [entry('project_settings'), entry('delete'), entry('send_feedback')],
  ]
}

/** Fold per-node capability answers (undefined = node lacks it) into one menu state. */
export function combineCapability(perNode: ReadonlyArray<{ enabled: boolean } | undefined>): CapabilityState {
  let present = false
  for (const action of perNode) {
    if (!action) continue
    if (action.enabled) return 'enabled'
    present = true
  }
  return present ? 'disabled' : 'hidden'
}

export function buildMenu(
  kind: ContextMenuKind,
  canvas: CanvasMenuState,
  node: NodeMenuState,
): ContextMenuSection[] {
  return kind === 'canvas' ? buildCanvasMenu(canvas) : buildNodeMenu(node)
}
